import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, UserPlus, Trash2, Crown, User, Loader2, ArrowLeft, Pencil, Save, Building, Globe, Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const INVESTMENT_FOCUS_OPTIONS = [
  "Multifamily Value-Add",
  "Multifamily Core-Plus",
  "Student Housing",
  "Workforce Housing",
  "Affordable / LIHTC",
  "Mixed-Use",
];

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
};

interface Member {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
  full_name: string;
}

export default function WorkspaceDetail() {
  const { id: workspaceId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState<{ id: string; name: string; created_by: string; company?: string; company_linkedin_url?: string; investment_focus?: string } | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);

  // Edit name
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Invite
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  // Business details
  const [bizCompany, setBizCompany] = useState("");
  const [bizLinkedin, setBizLinkedin] = useState("");
  const [bizFocus, setBizFocus] = useState("");
  const [savingBiz, setSavingBiz] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/auth"); return; }
      setCurrentUserId(user.id);
    };
    init();
  }, [navigate]);

  useEffect(() => {
    if (!workspaceId || !currentUserId) return;
    fetchWorkspace();
    fetchMembers();
  }, [workspaceId, currentUserId]);

  const fetchWorkspace = async () => {
    const { data, error } = await supabase
      .from("workspaces")
      .select("id, name, created_by, company, company_linkedin_url, investment_focus")
      .eq("id", workspaceId!)
      .single();

    if (error || !data) {
      toast({ title: "Workspace not found", variant: "destructive" });
      navigate("/profile");
      return;
    }
    setWorkspace(data as any);
    setNameValue(data.name);
    setBizCompany((data as any).company || "");
    setBizLinkedin((data as any).company_linkedin_url || "");
    setBizFocus((data as any).investment_focus || "");
  };

  const fetchMembers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("workspace_members")
      .select("id, user_id, role, created_at")
      .eq("workspace_id", workspaceId!);

    if (error) { setLoading(false); return; }

    const userIds = (data || []).map((m) => m.user_id);
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, full_name")
      .in("id", userIds);

    const profileMap = new Map((profiles || []).map((p) => [p.id, p.full_name || ""]));

    const enriched: Member[] = (data || []).map((m) => ({
      ...m,
      full_name: profileMap.get(m.user_id) || "Unknown",
    }));

    setMembers(enriched);
    setIsOwner(enriched.some((m) => m.user_id === currentUserId && m.role === "owner"));
    setLoading(false);
  };

  const handleSaveName = async () => {
    if (!nameValue.trim() || !workspace) return;
    setSavingName(true);
    // .select() so we can tell an accepted write from a refused one. RLS does
    // not raise when a row is out of reach — the update simply matches nothing,
    // and without this the UI would report success and then show the old name
    // again on the next load.
    const { data, error } = await supabase
      .from("workspaces")
      .update({ name: nameValue.trim(), updated_at: new Date().toISOString() })
      .eq("id", workspace.id)
      .select("id");

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else if (!data || data.length === 0) {
      toast({
        title: "Not saved",
        description: "Only a workspace owner can rename this workspace.",
        variant: "destructive",
      });
    } else {
      toast({ title: "Workspace name updated" });
      setWorkspace({ ...workspace, name: nameValue.trim() });
      setEditing(false);
    }
    setSavingName(false);
  };

  const handleSaveBiz = async () => {
    if (!workspace) return;
    setSavingBiz(true);
    const { data, error } = await supabase
      .from("workspaces")
      .update({
        company: bizCompany.trim() || null,
        company_linkedin_url: bizLinkedin.trim() || null,
        investment_focus: bizFocus || null,
        updated_at: new Date().toISOString(),
      } as any)
      .eq("id", workspace.id)
      .select("id");

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else if (!data || data.length === 0) {
      toast({
        title: "Not saved",
        description: "Only a workspace owner can edit these details.",
        variant: "destructive",
      });
    } else {
      toast({ title: "Business details updated" });
      setWorkspace({ ...workspace, company: bizCompany.trim(), company_linkedin_url: bizLinkedin.trim(), investment_focus: bizFocus });
    }
    setSavingBiz(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !workspaceId) return;
    setInviting(true);
    try {
      const res = await supabase.functions.invoke("workspace-invite", {
        body: { email: inviteEmail.trim(), workspace_id: workspaceId },
      });

      if (res.error) {
        toast({ title: "Invite failed", description: res.error.message, variant: "destructive" });
      } else if (res.data?.error) {
        toast({ title: "Invite failed", description: res.data.error, variant: "destructive" });
      } else {
        toast({ title: "Member added", description: `${inviteEmail} has been added.` });
        setInviteEmail("");
        fetchMembers();
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberId: string, memberName: string) => {
    const { error } = await supabase.from("workspace_members").delete().eq("id", memberId);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Member removed", description: `${memberName} has been removed.` });
      fetchMembers();
    }
  };

  if (loading && !workspace) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="px-6 py-10 sm:py-16 max-w-3xl mx-auto w-full">
        <motion.div {...fadeUp} className="mb-10">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 mb-4 font-mono text-xs text-muted-foreground"
            onClick={() => navigate("/profile")}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Profile
          </Button>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card mb-6">
            <Users className="w-3.5 h-3.5 text-accent" />
            <span className="font-mono text-xs text-muted-foreground">Workspace</span>
          </div>

          {/* Workspace Name */}
          {editing && isOwner ? (
            <div className="flex items-center gap-3 mb-3">
              <Input
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                className="font-display text-2xl font-bold h-12 max-w-md"
                onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
              />
              <Button onClick={handleSaveName} disabled={savingName} size="sm">
                {savingName ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                <span className="ml-1">Save</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setNameValue(workspace?.name || ""); }}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 mb-3">
              <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight tracking-tight">
                {workspace?.name}
              </h1>
              {isOwner && (
                <Button variant="ghost" size="icon" className="text-muted-foreground" onClick={() => setEditing(true)}>
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
          <p className="text-muted-foreground leading-relaxed max-w-lg">
            {isOwner ? "Manage your workspace name and team members." : "View workspace details and members."}
          </p>
        </motion.div>

        <div className="space-y-8">
          {/* Business Details */}
          <motion.div {...fadeUp}>
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg flex items-center gap-2">
                  <Building className="w-4 h-4 text-accent" />
                  Business Details
                </CardTitle>
                <CardDescription className="font-mono text-xs">
                  {isOwner ? "Company information linked to this workspace." : "Company information for this workspace."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isOwner ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Company / Firm</Label>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input value={bizCompany} onChange={(e) => setBizCompany(e.target.value)} placeholder="Acme Capital LLC" className="pl-10 font-mono" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Company LinkedIn</Label>
                        <div className="relative">
                          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input value={bizLinkedin} onChange={(e) => setBizLinkedin(e.target.value)} placeholder="https://linkedin.com/company/acme" className="pl-10 font-mono" />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Investment Focus</Label>
                      <Select value={bizFocus} onValueChange={setBizFocus}>
                        <SelectTrigger className="font-mono"><SelectValue placeholder="Select focus area" /></SelectTrigger>
                        <SelectContent>
                          {INVESTMENT_FOCUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleSaveBiz} disabled={savingBiz} size="sm" className="gap-1.5 font-display">
                      {savingBiz ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      {savingBiz ? "Saving…" : "Save Details"}
                    </Button>
                  </>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-muted-foreground" />
                      <span className="font-mono text-xs text-muted-foreground uppercase w-28">Company</span>
                      <span>{workspace?.company || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      <span className="font-mono text-xs text-muted-foreground uppercase w-28">LinkedIn</span>
                      <span>{workspace?.company_linkedin_url || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-muted-foreground" />
                      <span className="font-mono text-xs text-muted-foreground uppercase w-28">Focus</span>
                      <span>{workspace?.investment_focus || "—"}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Invite — owners only */}
          {isOwner && (
            <motion.div {...fadeUp}>
              <Card>
                <CardHeader>
                  <CardTitle className="font-display text-lg flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-accent" />
                    Invite Member
                  </CardTitle>
                  <CardDescription className="font-mono text-xs">
                    Add an existing Brickshare user by email. They'll join as a Member.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Label className="sr-only">Email address</Label>
                      <Input
                        type="email"
                        placeholder="colleague@company.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                        className="font-mono"
                      />
                    </div>
                    <Button onClick={handleInvite} disabled={!inviteEmail.trim() || inviting} className="gap-1.5 font-display">
                      {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                      {inviting ? "Inviting…" : "Invite"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Members List */}
          <motion.div {...fadeUp}>
            <Card>
              <CardHeader>
                <CardTitle className="font-display text-lg">Members ({members.length})</CardTitle>
                <CardDescription className="font-mono text-xs">All users with access to this workspace.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No members found.</p>
                ) : (
                  <div className="space-y-1">
                    {members.map((member, i) => (
                      <div key={member.id}>
                        {i > 0 && <Separator className="my-1" />}
                        <div className="flex items-center gap-3 py-3 px-2 rounded-md hover:bg-muted/50 transition-colors">
                          <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                            {member.role === "owner" ? (
                              <Crown className="w-3.5 h-3.5 text-accent" />
                            ) : (
                              <User className="w-3.5 h-3.5 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">
                                {member.full_name}
                                {member.user_id === currentUserId && (
                                  <span className="text-muted-foreground ml-1">(you)</span>
                                )}
                              </span>
                              <Badge variant={member.role === "owner" ? "default" : "secondary"} className="font-mono text-2xs uppercase">
                                {member.role}
                              </Badge>
                            </div>
                            <p className="font-mono text-2xs text-muted-foreground">
                              Joined {new Date(member.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {isOwner && member.user_id !== currentUserId && member.role !== "owner" && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-destructive">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove member?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {member.full_name} will lose access to this workspace and its shared deals.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleRemove(member.id, member.full_name)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
